<template>
  <Teleport to="body">
    <div
      v-if="layerState.isDragging && showCustomPreview && !suppressAfterDragEnd && layerState.currentOffset"
      class="custom-drag-layer"
    >
      <div :style="cardStyle" class="custom-drag-layer__wrapper">
        <div
          v-if="selectedCount > 1"
          class="custom-drag-layer__card custom-drag-layer__card--shadow"
        ></div>
        <div class="custom-drag-layer__card">
          <img
            v-if="iconSrc"
            class="custom-drag-layer__icon"
            :src="iconSrc"
          />
          <span class="custom-drag-layer__text">{{ displayName }}</span>
        </div>
        <span v-if="selectedCount > 1" class="custom-drag-layer__badge">{{ selectedCount }}</span>
        <div v-if="hasLockedProjection" class="custom-drag-layer__tooltip">
          {{ $t('projection_move_with_selection') }}
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, unref, watch } from 'vue'
import { useDragLayer } from 'vue3-dnd'
import { BaseItemTypes } from './ItemTypes'
import selectionIconSvg from '@/assets/selection-icon.svg'
import projectionIconSvg from '@/assets/projection-icon.svg'
import listIconSvg from '@/assets/list-icon.svg'
import pieChartIconSvg from '@/assets/pie-chart-icon.svg'
import barChartIconSvg from '@/assets/bar-chart-icon.svg'
import lineChartIconSvg from '@/assets/line-chart-icon.svg'
import folderClosedSvg from '@/assets/folder-closed.svg'

const props = defineProps<{
  selectedItems?: Map<string, any>
  flatItems?: Array<{ item: any; key: string }>
  /** Общее количество эффективно выбранных (реальные + потомки). */
  effectiveCount?: number
}>()

function makeSelectionKey(p: any): string {
  const elType = p.elType ?? ''
  const id = p.id ?? p.Id ?? ''
  const folderId = p.folderId ?? p.FolderId ?? ''
  const src = p.sourceDataType ?? p.SrcType ?? ''
  const dest = p.destinationDataType ?? p.DestType ?? ''
  // Согласовано с VirtualAnalyticsList/Analytics.makeElementLockKey: для дочерних элементов в отчётах
  // нужно различать конкретные ссылки (одна и та же сущность может встречаться в разных отчётах).
  const innerElementId = p.innerElementId ?? p.ElementId ?? ''
  return `${elType}:${id}:${folderId}:${src}:${dest}:${innerElementId}`
}

function iconForElType(elType: number | undefined): string {
  switch (elType) {
    case BaseItemTypes.SELECTION: return selectionIconSvg
    case BaseItemTypes.PROJECTION: return projectionIconSvg
    case BaseItemTypes.LIST: return listIconSvg
    case BaseItemTypes.PIE_CHART: return pieChartIconSvg
    case BaseItemTypes.BAR_CHART: return barChartIconSvg
    case BaseItemTypes.GRAPH: return lineChartIconSvg
    case BaseItemTypes.FOLDER: return folderClosedSvg
    default: return ''
  }
}

const layerState = useDragLayer((monitor) => ({
  isDragging: monitor.isDragging(),
  item: monitor.getItem(),
  currentOffset: monitor.getClientOffset(),
}))

// Страховка от залипшего превью: при сорванном жесте native dragend может прийти,
// а monitor.isDragging() ещё true — скрываем кастомный ghost до следующего drag.
const suppressAfterDragEnd = ref(false);

watch(() => unref(layerState).isDragging, (dragging) => {
  if (dragging) suppressAfterDragEnd.value = false;
});

onMounted(() => {
  document.addEventListener('dragend', () => {
    suppressAfterDragEnd.value = true;
  }, { capture: true });
});

const showCustomPreview = computed(() => {
  const state = unref(layerState)
  const cp = state.item?.callbackParams
  // Рисуем только элементы дерева аналитики: у них есть callbackParams.
  return !!cp
})

const selectedCount = computed(() => props.effectiveCount ?? props.selectedItems?.size ?? 0)

const resolvedItem = computed(() => {
  const state = unref(layerState)
  if (!state.isDragging || !state.item) return null

  const sel = props.selectedItems
  if (sel && sel.size > 0 && props.flatItems) {
    for (const fi of props.flatItems) {
      const key = makeSelectionKey(fi.item)
      if (sel.has(key)) {
        return { name: fi.item.Name ?? fi.item.CombinedName ?? '', elType: fi.item.elType }
      }
    }
  }

  return {
    name: state.item.elementName ?? '',
    elType: state.item.callbackParams?.elType,
  }
})

const displayName = computed(() => resolvedItem.value?.name ?? '')
const iconSrc = computed(() => iconForElType(resolvedItem.value?.elType))

const hasLockedProjection = computed(() => {
  const sel = props.selectedItems
  if (sel && sel.size > 0) {
    for (const item of sel.values()) {
      if (
        item.elType === BaseItemTypes.PROJECTION &&
        item.parent_type === BaseItemTypes.SELECTION
      ) return true
    }
  }
  const state = unref(layerState)
  const cp = state.item?.callbackParams
  if (cp && cp.elType === BaseItemTypes.PROJECTION && cp.parent_type === BaseItemTypes.SELECTION) {
    return true
  }
  return false
})

const cardStyle = computed(() => {
  const state = unref(layerState)
  if (!state.currentOffset) return { display: 'none' }
  return {
    position: 'absolute' as const,
    top: '0px',
    left: '0px',
    transform: `translate(${state.currentOffset.x}px, ${state.currentOffset.y}px)`,
    willChange: 'transform',
  }
})
</script>

<style lang="scss" scoped>
.custom-drag-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10000;

  &__wrapper {
    position: relative;
  }

  &__card {
    position: relative;
    width: 200px;
    height: 44px;
    padding: 0 12px;
    border-radius: 12px;
    border: 1px solid #F2F2F2;
    background: #FFF;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
    display: flex;
    align-items: center;
    box-sizing: border-box;

    &--shadow {
      position: absolute;
      top: 4px;
      left: 4px;
      padding: 0;
      display: block;
    }
  }

  &__icon {
    width: 18px;
    min-width: 18px;
    height: 18px;
    margin-right: 8px;
    flex-shrink: 0;
  }

  &__text {
    color: var(--Text-color-text_main, #333);
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1;
  }

  &__badge {
    position: absolute;
    top: -8px;
    right: -8px;
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    background: #3F884F;
    color: #FFF;
    font-family: Roboto, sans-serif;
    font-size: 11px;
    font-weight: 500;
    line-height: 20px;
    text-align: center;
    padding: 0 5px;
    box-sizing: border-box;
  }

  &__tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    border-radius: 8px;
    background: var(--color-tooltip, rgba(92, 92, 92, 0.90));
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
    padding: 2px 8px;
    color: var(--Text-color-text-btn, #FFF);
    font-family: Roboto, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    white-space: nowrap;
  }
}
</style>
